import { Group, MeshPhongMaterial, Quaternion, Vector3 } from 'three';
import gsap, { Power2 } from 'gsap';

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import WebGLGroup from '../_app/cuchillo/3D/WebGLGroup';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { isTouch, isSmartphone } from '../_app/cuchillo/core/Basics';
import { MOUSE_SETTINGS } from '../_app/cuchillo/3D/WebGLObject';
import DebugPane from './DebugPane';
import ThumbnailObject from './ThumbnailObject';
import Scene from './Scene';
// import Bottle from './Bottle'; // placeholder comentat

// El cercle (radius/amplitud) i les escales dels thumbnails de Products van en funció de VH
// (Metrics.HEIGHT), igual que el character (--character-width en vh) → es mantenen proporcionals.
export const CIRCLE_SETTINGS = {
  radiusFactor: 0.4626, // × HEIGHT (abans 0.257 × WIDTH; 0.257*1440/800 = 0.4626 al disseny 1440×800)
  tilt: 1.91,
  scrollSpeed: 0.0015,
  zScale: 0.0002,
  velZSpeed: 0.0013,
  bokehMaxBlur: 0.012,
  bokehBlurPower: 0.00008,
};

const CIRCLE_SETTINGS_DEFAULTS = {
  radiusFactor: 0.4626, // × HEIGHT (vegeu CIRCLE_SETTINGS)
  tilt: 1.91,
  zScale: 0.0002,
  bokehMaxBlur: 0.012,
};

// Mòbil: radi més tancat i un punt més de profunditat que desktop (zNorm ja normalitza per
// alçada, així que aquests valors són directament comparables als de desktop, no leftovers).
export const CIRCLE_SETTINGS_MOBILE = {
  radiusFactor: 0.358,
  zScale: 0.0010,
  bokehMaxBlur: 0.015,
};

// export const BOTTLE_SETTINGS = { rotSpeedY: 0.0039 }; // placeholder comentat
const BOTTLE_SETTINGS = {};

const _worldPos = new Vector3();
const _ndcPos = new Vector3();
const _GROUP_LERP = 0.06;

// Alçada de disseny (1440×800). El radi del cercle (i per tant worldPos.z) escala amb HEIGHT,
// així que normalitzem el zScale per (DESIGN_HEIGHT / HEIGHT) → l'efecte de profunditat és
// idèntic a qualsevol alçada de pantalla (no es dispara en tablet portrait).
const DESIGN_HEIGHT = 800;

export default class ProductsScene {
  static group = new Group();
  static velocityGroup = new WebGLGroup({ rotFactor: 0.05 });
  static circleGroup = new Group();
  // static bottleGroup = new Group(); // placeholder comentat
  // static _bottle = null;            // placeholder comentat
  static items = [];
  static _scroller = null;
  static _N = 0;
  static _dir1Light = null;
  static _dir2Light = null;
  static _pointLight = null;
  // Llums gestionades per Scene (igual que a Home) — no creem llums pròpies

  static init(container, scroller) {
    if (!WebGLSketch.renderer) WebGLSketch.init();

    this._scroller = scroller;
    this._scroller.options.minSpeed = 60;

    Object.assign(CIRCLE_SETTINGS, CIRCLE_SETTINGS_DEFAULTS);
    // Només el smartphone (≤480) usa els settings mòbil. El TABLET ha d'anar com desktop
    // (abans amb isTouch el tablet agafava el zScale mòbil 14× → thumbnails gegants).
    if (isSmartphone) {
      CIRCLE_SETTINGS.radiusFactor = CIRCLE_SETTINGS_MOBILE.radiusFactor;
      CIRCLE_SETTINGS.zScale = CIRCLE_SETTINGS_MOBILE.zScale;
      CIRCLE_SETTINGS.bokehMaxBlur = CIRCLE_SETTINGS_MOBILE.bokehMaxBlur;
    }

    this.velocityGroup.add(this.circleGroup);
    this.group.add(this.velocityGroup);
    WebGLSketch.scene.add(this.group);

    // Layer de família segons el tipus de pàgina (luminaris L4 / obscurios L5); el mateix
    // layer que el helmet → thumbnails i tapon ordenen bé al mateix pass.
    const categ = container.dataset.type || 'luminaris';
    Scene.initProductLights(categ);

    const thumbEls = GetBy.class('__thumbnail', container);
    this._N = thumbEls.length;

    for (let i = 0; i < this._N; i++) {
      const thumb = new ThumbnailObject({
        dom: thumbEls[i],
        link: thumbEls[i].dataset.link || '',
        hasMouse: false,
      });

      thumb.init().then(() => Scene.setProductLayer(thumb, 'obscurios'));
      thumb.setOpacity(0);

      this.circleGroup.add(thumb);
      this.items.push(thumb);
    }

    DebugPane.addCircle(CIRCLE_SETTINGS, null); // bottle comentat
  }

  static show() {
    gsap.to(this._scroller.options, {
      minSpeed: 0.6,
      ease: Power2.easeOut,
      duration: 3,
      delay: 2,
    });

    gsap.to(
      this.items.map(item => item._op),
      { value: 1, duration: 0.6, stagger: 0.2, delay: 0.5 }
    );

    gsap.from(
      CIRCLE_SETTINGS,
      { radiusFactor: 0, tilt: 1.7, duration: 1, delay: .5, ease: Power2.easeOut }
    );

    // if (this._bottle?._material) { ... } // placeholder comentat
  }

  static hide() {
    gsap.killTweensOf(this._scroller.options);
    gsap.to(this._scroller.options, {
      minSpeed: 100,
      ease: Power2.easeOut,
      duration: 0.3,
    });

    gsap.killTweensOf(this.items.map(item => item._op));
    gsap.to(
      this.items.map(item => item._op),
      { value: 0, duration: 0.3, stagger: 0.08, delay: .2 }
    );

    gsap.to(
      CIRCLE_SETTINGS,
      { radiusFactor: .1, tilt: 2, duration: 1.5, delay: 0, ease: Power2.easeOut }
    );

    // if (this._bottle?._material) { ... } // placeholder comentat
  }

  static loop() {
    if (this._N === 0 || !this._scroller) return;

    const radius = Metrics.HEIGHT * CIRCLE_SETTINGS.radiusFactor;
    const vel = this._scroller.speed || 0;
    Scene.setScrollVelocity(vel); // character+tapon roten com bottleGroup

    this.circleGroup.rotation.x = CIRCLE_SETTINGS.tilt;
    this.circleGroup.rotation.z = this._scroller.position * CIRCLE_SETTINGS.scrollSpeed;

    this.velocityGroup.rot.z = vel * CIRCLE_SETTINGS.velZSpeed;
    this.velocityGroup.update();

    // if (this._bottle) { ... } // placeholder comentat

    const invQ = new Quaternion();
    this.circleGroup.getWorldQuaternion(invQ);
    invQ.invert();

    this.items.forEach((item, i) => {
      const angle = (i / this._N) * Math.PI * 2;
      item.pos.x = Math.cos(angle) * radius;
      item.pos.y = Math.sin(angle) * radius;
      item.pos.z = 0;
      item.webglGroup.quaternion.copy(invQ);
      item.update();

      item.getWorldPosition(_worldPos);
      const zNorm = DESIGN_HEIGHT / Metrics.HEIGHT; // profunditat independent de l'alçada
      const s = Math.max(0.01, 1 + _worldPos.z * CIRCLE_SETTINGS.zScale * zNorm);
      item.webglGroup.scale.setScalar(s);

      const blurAmount = Math.min(Math.max(0, -_worldPos.z) * CIRCLE_SETTINGS.bokehBlurPower, CIRCLE_SETTINGS.bokehMaxBlur);
      if (item.mesh) item.material.uniforms.blur.value = blurAmount;

      if (!isTouch && MOUSE_SETTINGS.enabled && item.mesh) {
        _ndcPos.copy(_worldPos).project(WebGLSketch.camera);
        const cx = (_ndcPos.x + 1) * 0.5 * Metrics.WIDTH;
        const cy = (1 - _ndcPos.y) * 0.5 * Metrics.HEIGHT;
        const w = item.mesh.scale.x;
        const h = item.mesh.scale.y;
        const nx = (Interaction.positions.mouse.x - cx) / (w * 0.5);
        const ny = (Interaction.positions.mouse.y - cy) / (h * 0.5);
        const isOver = Math.abs(nx) <= 1 && Math.abs(ny) <= 1;
        const tnx = isOver ? nx : 0;
        const tny = isOver ? ny : 0;
        const targetRotX = Maths.toRadians(tny * MOUSE_SETTINGS.rotX);
        const targetRotY = Maths.toRadians(tnx * MOUSE_SETTINGS.rotY);
        const targetPosX = -tnx * MOUSE_SETTINGS.displaceX;
        const targetPosY = tny * MOUSE_SETTINGS.displaceY;
        let targetPosZ = 0;
        if (isOver) {
          const dist = Math.sqrt(tnx * tnx + tny * tny);
          targetPosZ = -Maths.clamp(1 - dist, 0, 1) * MOUSE_SETTINGS.displaceZ;
        }
        item.rotationGroup.rotation.x = Maths.lerp(item.rotationGroup.rotation.x, targetRotX, _GROUP_LERP);
        item.rotationGroup.rotation.y = Maths.lerp(item.rotationGroup.rotation.y, targetRotY, _GROUP_LERP);
        item.rotationGroup.position.x = Maths.lerp(item.rotationGroup.position.x, targetPosX, _GROUP_LERP);
        item.rotationGroup.position.y = Maths.lerp(item.rotationGroup.position.y, targetPosY, _GROUP_LERP);
        item.rotationGroup.position.z = Maths.lerp(item.rotationGroup.position.z, targetPosZ, _GROUP_LERP);
      }
    });
  }

  static resize() {
    // this._bottle?.resize(); // placeholder comentat
    this.items.forEach(item => item.resize());
    // Llums de producte les redimensiona Scene.resize()
  }

  static dispose() {
    DebugPane.removeCircle();
    Scene.disposeProductLights(); // llums gestionades per Scene

    gsap.killTweensOf(CIRCLE_SETTINGS);
    if (this._scroller) {
      gsap.killTweensOf(this._scroller.options);
      this._scroller = null;
    }
    this._N = 0;

    // if (this._bottle) { ... } // placeholder comentat

    this.items.forEach(item => {
      gsap.killTweensOf(item._op);
      this.circleGroup.remove(item);
      item.dispose();
    });
    this.items = [];
    this.circleGroup.rotation.set(0, 0, 0);
    this.velocityGroup.rotation.set(0, 0, 0);
    this.velocityGroup.rot.set(0, 0, 0);
    this.velocityGroup.remove(this.circleGroup);
    this.group.remove(this.velocityGroup);
    WebGLSketch.scene.remove(this.group);
  }
}
